import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../api/apiClient';
import { API_URL, isDevelopment } from '../config/env';
import { createSocket, socketConfig } from '../config/socket';
import { Socket } from 'socket.io-client';
import { getAuthToken } from '../utils/authToken';

interface HealthDetails {
  redis?: { status: string; message?: string };
  database?: { status: string; message?: string };
}

const HealthCheck: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [backendUrl, setBackendUrl] = useState<string>('');
  const [healthDetails, setHealthDetails] = useState<HealthDetails | null>(null);
  const [overallStatus, setOverallStatus] = useState<'ok' | 'degraded' | null>(null);
  const [socketStatus, setSocketStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [socketErrorMessage, setSocketErrorMessage] = useState<string>('');
  const [socketId, setSocketId] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const checkBackendConnection = async () => {
      try {
        // Log the actual URL being requested
        const fullUrl = isDevelopment ? '/api/health' : `${API_URL}/api/health`;
        setBackendUrl(fullUrl);
        
        console.log('[HealthCheck] Attempting to connect to:', fullUrl);
        console.log('[HealthCheck] isDevelopment:', isDevelopment);
        console.log('[HealthCheck] API_URL:', API_URL);
        
        // Try to ping the backend health check endpoint (includes Redis + MySQL checks)
        const response = await apiClient.get('/api/health');
        if (response.status === 200) {
          setConnectionStatus('connected');
          setErrorMessage('');
          setOverallStatus(response.data.status ?? 'ok');
          setHealthDetails({
            redis: response.data.redis,
            database: response.data.database,
          });
        } else {
          setConnectionStatus('disconnected');
          setErrorMessage(`Backend responded with status: ${response.status}`);
          setHealthDetails(null);
          setOverallStatus(null);
        }
      } catch (error: any) {
        setConnectionStatus('disconnected');
        
        // Enhanced error logging
        console.error('[HealthCheck] Backend connection error:', error);
        console.error('[HealthCheck] Error details:', {
          message: error.message,
          code: error.code,
          response: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers
          } : null,
          request: error.request ? {
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            fullURL: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url
          } : null
        });
        
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const statusText = error.response.statusText;
          const data = error.response.data;
          
          if (status === 0) {
            // Status 0 often indicates CORS or network issue
            setErrorMessage(`CORS or Network Error (Status 0) - Check if backend URL is correct and CORS is configured`);
          } else {
            setErrorMessage(`HTTP ${status} ${statusText}: ${data?.message || JSON.stringify(data)}`);
          }
        } else if (error.request) {
          // Request made but no response received
          const requestUrl = error.config?.baseURL 
            ? `${error.config.baseURL}${error.config.url}` 
            : error.config?.url || 'unknown';
          
          // Check for mixed content (HTTPS page trying to access HTTP)
          if (window.location.protocol === 'https:' && requestUrl.startsWith('http://')) {
            setErrorMessage(`Mixed Content Error: HTTPS page cannot access HTTP backend. Backend URL must use HTTPS.`);
          } else {
            setErrorMessage(`No response from backend at ${requestUrl} - server may be down, unreachable, or blocked by CORS`);
          }
        } else if (error.message) {
          // Something else happened
          if (error.message.includes('Network Error') || error.message.includes('CORS')) {
            setErrorMessage(`Network/CORS Error: ${error.message} - Check backend CORS configuration and ensure backend URL uses HTTPS`);
          } else {
            setErrorMessage(`Error: ${error.message}`);
          }
        } else {
          setErrorMessage('Unknown error occurred');
        }
      }
    };

    checkBackendConnection();
  }, []);

  useEffect(() => {
    // Create socket connection for testing – one token only (user or guest JWT)
    const token = getAuthToken();
    const socket = createSocket(token ? { token } : {});
    socketRef.current = socket;

    // Set up event handlers
    const handleConnect = () => {
      setSocketStatus('connected');
      setSocketId(socket.id || null);
      setSocketErrorMessage('');
    };

    const handleDisconnect = (reason: string) => {
      setSocketStatus('disconnected');
      setSocketErrorMessage(`Disconnected: ${reason}`);
      setSocketId(null);
    };

    const handleConnectError = (error: Error) => {
      setSocketStatus('disconnected');
      setSocketErrorMessage(`Connection error: ${error.message}`);
      setSocketId(null);
    };

    const handleReconnect = (_attemptNumber: number) => {
      setSocketStatus('connected');
      setSocketId(socket.id || null);
      setSocketErrorMessage('');
    };

    const handleReconnectError = (error: Error) => {
      setSocketStatus('disconnected');
      setSocketErrorMessage(`Reconnection error: ${error.message}`);
    };

    const handleReconnectFailed = () => {
      setSocketStatus('disconnected');
      setSocketErrorMessage('Reconnection failed after all attempts');
    };

    // Attach event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('reconnect', handleReconnect);
    socket.on('reconnect_error', handleReconnectError);
    socket.on('reconnect_failed', handleReconnectFailed);

    // Check initial connection state
    let timeout: NodeJS.Timeout | null = null;
    if (socket.connected) {
      handleConnect();
    } else {
      // Set a timeout to mark as disconnected if not connected within reasonable time
      timeout = setTimeout(() => {
        if (!socket.connected) {
          setSocketStatus('disconnected');
          setSocketErrorMessage('Connection timeout - socket did not connect');
        }
      }, 10000); // 10 second timeout
    }

    // Cleanup function
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('reconnect', handleReconnect);
      socket.off('reconnect_error', handleReconnectError);
      socket.off('reconnect_failed', handleReconnectFailed);
      socket.disconnect();
    };
  }, []);

  const getStatusColor = (status: 'checking' | 'connected' | 'disconnected') => {
    switch (status) {
      case 'checking':
        return '#FFA500'; // Orange
      case 'connected':
        return '#00FF00'; // Green
      case 'disconnected':
        return '#FF0000'; // Red
    }
  };

  const getStatusText = (status: 'checking' | 'connected' | 'disconnected', type: 'backend' | 'socket') => {
    if (type === 'backend') {
      switch (status) {
        case 'checking':
          return 'Checking backend connection...';
        case 'connected':
          return '✅ Successfully connected to backend!';
        case 'disconnected':
          return '❌ Failed to connect to backend';
      }
    } else {
      switch (status) {
        case 'checking':
          return 'Checking socket connection...';
        case 'connected':
          return '✅ Successfully connected to socket!';
        case 'disconnected':
          return '❌ Failed to connect to socket';
      }
    }
  };

  const getBackendType = () => {
    if (isDevelopment) {
      return 'Local Backend';
    }
    // Check if API_URL contains localhost
    if (API_URL.includes('localhost') || API_URL.includes('127.0.0.1')) {
      return 'Local Backend';
    }
    return 'Production Backend';
  };

  const getBackendUrl = () => {
    if (isDevelopment) {
      // In dev, show the proxy target (which is the actual backend URL)
      return API_URL;
    }
    return API_URL;
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <h1 style={{ marginBottom: '20px' }}>Connection Status</h1>
      
      {/* Backend Connection Status */}
      <div style={{
        padding: '20px',
        border: `3px solid ${getStatusColor(connectionStatus)}`,
        borderRadius: '8px',
        backgroundColor: '#f5f5f5',
        minWidth: '300px',
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ marginTop: '0', marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>Backend API</h2>
        <p style={{ 
          fontSize: '18px', 
          fontWeight: 'bold',
          color: getStatusColor(connectionStatus),
          margin: '10px 0'
        }}>
          {getStatusText(connectionStatus, 'backend')}
        </p>
        {connectionStatus === 'connected' && (
          <>
            <div style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: overallStatus === 'degraded' ? '#fff8e1' : '#e8f5e9',
              borderRadius: '5px',
              border: `1px solid ${overallStatus === 'degraded' ? '#ffa000' : '#4caf50'}`
            }}>
              <p style={{ 
                fontSize: '14px', 
                fontWeight: 'bold',
                color: overallStatus === 'degraded' ? '#e65100' : '#2e7d32',
                margin: '5px 0'
              }}>
                Backend Type: {getBackendType()}
              </p>
              <p style={{ 
                fontSize: '12px', 
                color: '#388e3c',
                margin: '5px 0',
                wordBreak: 'break-all'
              }}>
                URL: {getBackendUrl()}
              </p>
              {overallStatus === 'degraded' && (
                <p style={{ fontSize: '12px', color: '#e65100', margin: '8px 0 0 0' }}>
                  One or more dependencies are failing (see below).
                </p>
              )}
            </div>
            {healthDetails && (healthDetails.redis || healthDetails.database) && (
              <div style={{
                marginTop: '12px',
                padding: '10px',
                backgroundColor: '#f5f5f5',
                borderRadius: '5px',
                border: '1px solid #e0e0e0',
                textAlign: 'left'
              }}>
                <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '0 0 8px 0', color: '#333' }}>
                  Dependencies
                </p>
                {healthDetails.redis && (
                  <p style={{ 
                    fontSize: '13px', 
                    margin: '4px 0',
                    color: healthDetails.redis.status === 'ok' ? '#2e7d32' : '#c62828'
                  }}>
                    Redis: {healthDetails.redis.status === 'ok' ? '✅ OK' : `❌ ${healthDetails.redis.message || 'Error'}`}
                  </p>
                )}
                {healthDetails.database && (
                  <p style={{ 
                    fontSize: '13px', 
                    margin: '4px 0',
                    color: healthDetails.database.status === 'ok' ? '#2e7d32' : '#c62828'
                  }}>
                    MySQL: {healthDetails.database.status === 'ok' ? '✅ OK' : `❌ ${healthDetails.database.message || 'Error'}`}
                  </p>
                )}
              </div>
            )}
          </>
        )}
        {errorMessage && (
          <div style={{ 
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#ffebee',
            borderRadius: '5px',
            border: '1px solid #c62828'
          }}>
            <p style={{ 
              fontSize: '14px', 
              color: '#c62828',
              fontWeight: 'bold',
              margin: '5px 0'
            }}>
              Error Details:
            </p>
            <p style={{ 
              fontSize: '13px', 
              color: '#666',
              margin: '5px 0',
              wordBreak: 'break-word'
            }}>
              {errorMessage}
            </p>
            {backendUrl && (
              <p style={{ 
                fontSize: '12px', 
                color: '#999',
                margin: '5px 0',
                wordBreak: 'break-all'
              }}>
                Attempted URL: {backendUrl}
              </p>
            )}
          </div>
        )}
        <div style={{
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#fff3e0',
          borderRadius: '5px',
          border: '1px solid #ff9800'
        }}>
          <p style={{ 
            fontSize: '12px', 
            color: '#e65100',
            margin: '5px 0'
          }}>
            Configured Backend: {getBackendType()}
          </p>
          <p style={{ 
            fontSize: '11px', 
            color: '#f57c00',
            margin: '5px 0',
            wordBreak: 'break-all'
          }}>
            {getBackendUrl()}
          </p>
        </div>
      </div>

      {/* Socket Connection Status */}
      <div style={{
        padding: '20px',
        border: `3px solid ${getStatusColor(socketStatus)}`,
        borderRadius: '8px',
        backgroundColor: '#f5f5f5',
        minWidth: '300px',
        textAlign: 'center'
      }}>
        <h2 style={{ marginTop: '0', marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>Socket.IO Connection</h2>
        <p style={{ 
          fontSize: '18px', 
          fontWeight: 'bold',
          color: getStatusColor(socketStatus),
          margin: '10px 0'
        }}>
          {getStatusText(socketStatus, 'socket')}
        </p>
        {socketStatus === 'connected' && (
          <div style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#e8f5e9',
            borderRadius: '5px',
            border: '1px solid #4caf50'
          }}>
            <p style={{ 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: '#2e7d32',
              margin: '5px 0'
            }}>
              Socket ID: {socketId || 'N/A'}
            </p>
            <p style={{ 
              fontSize: '12px', 
              color: '#388e3c',
              margin: '5px 0',
              wordBreak: 'break-all'
            }}>
              URL: {socketConfig.url}
            </p>
          </div>
        )}
        {socketErrorMessage && (
          <p style={{ 
            fontSize: '14px', 
            color: '#666',
            marginTop: '10px'
          }}>
            {socketErrorMessage}
          </p>
        )}
        <div style={{
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#fff3e0',
          borderRadius: '5px',
          border: '1px solid #ff9800'
        }}>
          <p style={{ 
            fontSize: '12px', 
            color: '#e65100',
            margin: '5px 0'
          }}>
            Configured Socket URL: {socketConfig.url}
          </p>
          <p style={{ 
            fontSize: '11px', 
            color: '#f57c00',
            margin: '5px 0'
          }}>
            Transports: {socketConfig.options.transports?.join(', ') || 'default'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HealthCheck;

